/**
 * Trash Trail API client.
 *
 * TODO: once @dregsbane/web-types is published, replace the local type imports
 * with `import type {...} from '@dregsbane/web-types'`.
 *
 * All requests carry `credentials: 'include'` so the JWT cookie set by
 * the OAuth callbacks rides along. The cookie is HttpOnly — JS can't read
 * it, but the API will reject unauthenticated reads which is how the UI
 * detects "no session".
 *
 * Standalone-PWA fallback: iOS home-screen apps don't reliably keep the
 * cookie, so when lib/session-token.ts holds a stored session JWT it is
 * also attached as `Authorization: Bearer <jwt>` (accepted by every API
 * endpoint as a cookie alternative). See session-token.ts for the
 * storage policy.
 */
import type {
  AccountStats,
  Detection,
  HazardPin,
  MyCleanup,
  OsmPrefill,
  Submission,
  SubmissionInput,
  User,
  DrawnPath,
  Hazard,
} from './types';
import type { AmountBucket } from './cleanup-types';
import { clearToken, getStoredToken, isStandalone, storeToken } from './session-token';
import { clearLocalUserData } from './clear-local-user-data';

// Pick API host from the build-time env. The marketing site / PWA build
// pipelines set VITE_API_BASE per environment.
const API_BASE: string =
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_BASE ??
  (location.hostname === 'trail.dregsbane.com'
    ? 'https://api.dregsbane.com'
    : (location.hostname === 'localhost' || location.hostname === '127.0.0.1') &&
        (import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE === 'development'
      ? // vite dev only (MODE gate keeps vitest/happy-dom on the real
        // host): same-origin /api + /auth, forwarded by the dev proxy in
        // vite.config.ts — the beta API's CORS allowlist is the beta
        // origin only, so a direct cross-origin call from localhost fails.
        ''
      : 'https://api.beta.dregsbane.com');

/** Bearer fallback for the standalone PWA (see lib/session-token.ts). */
function authHeader(): Record<string, string> {
  const token = getStoredToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Spread `rest` BEFORE `headers` — spreading the whole init after the
  // headers key would clobber the merged object with init.headers and
  // silently drop the auth/content-type defaults.
  const { headers: initHeaders, ...rest } = init;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...authHeader(),
      ...(initHeaders ?? {}),
    },
  });
  if (!res.ok) {
    throw new ApiError(res.status, await safeBody(res));
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function safeBody(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body || '(no body)'}`);
  }
}

// ─── Auth ──────────────────────────────────────────────────────────────────
// OAuth only. The email magic-link/OTP endpoints were retired with SES
// (2026-08) — the backend no longer routes /auth/request, /auth/verify or
// /auth/verify-code.

export type OAuthProvider = 'google' | 'apple';

/**
 * Which client is asking, carried to the backend as `?client=`.
 *
 * It is an opaque KEY, never a URL: the backend maps it to a destination
 * from a closed table compiled into the Lambda, so no caller — however
 * hostile — can express "send the session over there". Carrying a URL in
 * `state` or in a query param, even a signed one, is how open-redirect-to-
 * token-theft happens.
 */
export type OAuthClient = 'web' | 'ios';

/**
 * URL for GET /auth/{provider}/start — a FULL-PAGE navigation target
 * (anchor href / location.assign / a native auth sheet), never fetch: the
 * endpoint 302s to the provider's consent screen, which fetch+CORS can't
 * follow.
 *
 * On failure the backend bounces back to /sign-in?error=<code>; on success
 * it lands on /auth/complete#token=…&next=… (web, the session JWT) or on
 * com.dregsbane.trail://auth/complete#handoff=…&next=… (client=ios, a
 * 90-second single-use code traded at POST /auth/handoff) — see
 * pages/auth-complete.tsx and lib/native-auth.ts.
 *
 * The native flow also appends `cc=<PKCE S256 challenge>` to this URL. It
 * is added inside lib/native-auth.ts rather than here, because the
 * verifier it commits to must never leave that module.
 */
export function oauthSignInStartUrl(
  provider: OAuthProvider,
  opts: { next?: string; client?: OAuthClient } = {},
): string {
  const params = new URLSearchParams();
  if (opts.next) params.set('next', opts.next);
  // `web` is the server-side default; omitting it keeps the web URL
  // byte-identical to what shipped, so the existing anchor assertions and
  // any bookmarked start URLs are untouched.
  if (opts.client && opts.client !== 'web') params.set('client', opts.client);
  const qs = params.toString();
  return `${API_BASE}/auth/${provider}/start${qs ? `?${qs}` : ''}`;
}

export function googleSignInStartUrl(next?: string, client?: OAuthClient): string {
  return oauthSignInStartUrl('google', { next, client });
}

/**
 * Apple's twin. `appleSignInEnabled()` (lib/feature-flags.ts) defaults
 * OFF until the env has the Apple routes deployed AND the SSM config —
 * see that file for the full precondition list.
 */
export function appleSignInStartUrl(next?: string, client?: OAuthClient): string {
  return oauthSignInStartUrl('apple', { next, client });
}

export interface HandoffExchangeResult {
  /** The real 30-day session JWT. */
  token: string;
  /** Server-bound post-sign-in path. */
  next: string;
}

/**
 * Trade a native single-use handoff for the session — POST /auth/handoff.
 *
 * The native OAuth callback comes back over the OS URL-scheme channel,
 * which iOS does not make exclusive to us. So it carries a 90-second
 * single-use code, not a session, and this call is where the session is
 * actually obtained: over TLS, on a connection no other app can read.
 *
 * `verifier` is the PKCE (RFC 7636 S256) pre-image of the challenge the
 * app sent at /auth/{provider}/start. It is the reason an intercepted
 * handoff is worthless rather than merely short-lived — it never travels
 * on the channel the handoff travels on, and it is generated and held
 * inside lib/native-auth.ts, which is the only caller of this function.
 * Do not export it, log it, or persist it.
 *
 * Deliberately NOT via `request()`: that helper attaches whatever bearer
 * token is already stored, and this call establishes the session — there
 * is nothing to attach and sending a stale one would be noise. Throws on
 * any non-200 so the caller shows a failed sign-in; the backend answers
 * every rejection identically (unknown / consumed / expired / wrong
 * verifier are one 401) so there is nothing here to branch on.
 */
export async function exchangeAuthHandoff(
  handoff: string,
  verifier?: string,
): Promise<HandoffExchangeResult> {
  const res = await fetch(`${API_BASE}/auth/handoff`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(verifier ? { handoff, verifier } : { handoff }),
  });
  if (!res.ok) throw new ApiError(res.status, await safeBody(res));
  const parsed = (await res.json()) as Partial<HandoffExchangeResult> & { ok?: boolean };
  if (!parsed.ok || typeof parsed.token !== 'string' || typeof parsed.next !== 'string') {
    throw new ApiError(res.status, 'malformed handoff exchange response');
  }
  return { token: parsed.token, next: parsed.next };
}

export function getCurrentUser(): Promise<User | null> {
  // /auth/me may include a fresh `token` when the backend sliding-renews
  // the session; the remaining fields are the plain User shape.
  return request<User & { token?: string }>('/auth/me')
    .then((user) => {
      // Keep the persisted JWT fresh: replace a token that is already
      // stored, or adopt the renewal in standalone mode (bridges a
      // cookie-only session that predates bearer persistence).
      if (user.token && (getStoredToken() !== null || isStandalone())) {
        storeToken(user.token);
      }
      return user;
    })
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 401) {
        // The session is dead server-side — a stored bearer token is now
        // stale (it rode along on this very request), so drop it.
        clearToken();
        return null;
      }
      throw err;
    });
}

/**
 * Delete the signed-in account. Contributions are anonymised rather than
 * erased — reports already published under CC-BY-SA-4.0 cannot be recalled,
 * and deleting the rows outright would tear holes in a public dataset other
 * people have already built on. The server unlinks them from the user and
 * scrubs the photos.
 *
 * Required by App Store Guideline 5.1.1(v): an app that supports account
 * creation must support deletion from inside the app.
 */
export async function deleteAccount(): Promise<{ anonymized_contributions: number }> {
  const res = await request<{ ok: true; anonymized_contributions: number }>('/account', {
    method: 'DELETE',
  });
  // Drop the bearer token regardless of how the caller navigates next: the
  // server has expired the cookie, and a stored JWT for a deleted user
  // would otherwise sit in localStorage 401ing every request.
  clearToken();
  // The server just purged this user's queue and pins; the local copies
  // are orphans now, and on a shared device they'd leak to the next user.
  await clearLocalUserData();
  return { anonymized_contributions: res.anonymized_contributions };
}

/**
 * Sign out: two credentials, two clears. POST /auth/signout expires the
 * HttpOnly cookie (only a server response can — JS never sees it), and
 * the stored bearer token is dropped locally.
 *
 * The local clear runs in `finally`: sign-out must never trap the user.
 * If the network call fails, this device still forgets the credential and
 * the UI reads signed-out; the cookie (if any survives) dies at its
 * 30-day expiry — the JWT was never revocable server-side anyway (no
 * session table).
 */
/** The signed-in user's impact ledger (GET /account/stats). */
export function getAccountStats(): Promise<AccountStats> {
  return request<{ stats: AccountStats }>('/account/stats').then((r) => r.stats);
}

/** The signed-in user's cleanup history, newest first (GET /cleanups/mine). */
export function getMyCleanups(): Promise<MyCleanup[]> {
  return request<{ cleanups: MyCleanup[] }>('/cleanups/mine').then(
    (r) => r.cleanups,
  );
}

export async function signOut(): Promise<void> {
  try {
    await request<{ ok: true }>('/auth/signout', { method: 'POST' });
  } finally {
    clearToken();
    // Identity-scoped local data goes with the credential (own pins,
    // recent submissions, queued photos) — shared-device hygiene. The
    // account page warns about pending uploads BEFORE calling this.
    await clearLocalUserData();
  }
}

// ─── Submissions ───────────────────────────────────────────────────────────

export interface PresignedUpload {
  /** Final S3 key the bytes will land at, before EXIF strip. */
  key: string;
  /** Presigned PUT URL, single-use within `expires_at`. */
  url: string;
  /** ISO timestamp — the URL stops working after this. */
  expires_at: string;
}

export interface CreateSubmissionResponse {
  /** ULID of the new submission row. */
  id: string;
  /** One presigned PUT, in upload order. */
  upload_urls: PresignedUpload[];
  /**
   * Trail manifest version after this submission (V1 contract §1). Present
   * even when no map pin was written (geo omitted) — it is then simply the
   * current version. Typed optional so the client stays tolerant of the
   * previously-deployed backend during the deploy window.
   */
  manifest_version?: string;
}

export function createSubmission(
  data: SubmissionInput,
): Promise<CreateSubmissionResponse> {
  return request<CreateSubmissionResponse>('/submissions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Cleanup events ──────────────────────────────────────────────────────────
//
// Cleanup Ledger P1 — see docs/cleanup-ledger-spec.md. A claim ledger,
// not a dispatch workflow: logging a cleanup is a single POST, no
// assignment/status flow.

export interface LogCleanupEventPayload {
  /** Anchored to the stable observation id — never a merged mound id. */
  scene_observation_id: string;
  amount_bucket: AmountBucket;
  on_behalf_of_org_id?: string;
  /**
   * Volunteer effort (issue #55) — DOEE / NOAA / 2 CFR 200.306 reporting
   * inputs. Always optional; the bucket picker only offers them on an
   * on-behalf-of log (org-select.ts), though the backend accepts them on
   * individual claims too. volunteer_hours = TOTAL volunteer-hours
   * across the crew (fractional ok), not event duration. Server
   * re-validates: > 0, fat-finger caps.
   */
  volunteer_count?: number;
  volunteer_hours?: number;
  /** Idempotent-replay key for a client that retries after a dropped response. */
  client_event_id?: string;
}

export interface LogCleanupEventResponse {
  id: string;
  /** Server-computed from the bucket midpoint — never client-supplied. */
  items_estimate: number;
  verified: boolean;
}

export function logCleanupEvent(
  payload: LogCleanupEventPayload,
): Promise<LogCleanupEventResponse> {
  return request<LogCleanupEventResponse>('/cleanup-events', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Org endpoints live in api-orgs.ts — split out of this module so the
// entry chunk (which owns api.ts via session) doesn't carry them; they
// load with the lazy /orgs page and the bucket-picker org selector
// (first-paint budget, spec §10).

/** Cleanup Ledger P2 — see docs/cleanup-ledger-spec.md. Optional
 *  photo-verification step, additive on top of the P1 claim above: a
 *  cleanup_events row is already durable before this is ever called. */
export interface AfterPhotoUploadResponse {
  key: string;
  url: string;
  expires_at: string;
}

export function requestAfterPhotoUpload(
  cleanupEventId: string,
): Promise<AfterPhotoUploadResponse> {
  return request<AfterPhotoUploadResponse>(
    `/cleanup-events/${encodeURIComponent(cleanupEventId)}/after-photo`,
    { method: 'POST' },
  );
}

/**
 * Direct PUT to S3 via presigned URL. Does NOT include credentials — the
 * URL itself is the auth. Returns the upload's progress events via the
 * optional onProgress callback.
 *
 * Times out via xhr.timeout (default 60s per photo) so a CORS misconfig
 * or stuck connection surfaces as an error in the UI instead of a silent
 * spinner. The presigned URL itself has a 5-min TTL — anything that
 * takes longer than 60s on a single photo means something is wrong.
 */
export const PHOTO_UPLOAD_TIMEOUT_MS = 60_000;

export function uploadPhoto(
  presignedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
    xhr.timeout = PHOTO_UPLOAD_TIMEOUT_MS;
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      });
    }
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status, xhr.responseText));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'upload failed — check your connection')));
    xhr.addEventListener('timeout', () => reject(new ApiError(0, 'upload timed out')));
    xhr.send(file);
  });
}

export interface AnnotationPayload {
  drawn?: DrawnPath;
  hazards_to_avoid?: Hazard[];
  hazard_pins?: HazardPin[];
}

// NOTE on the route: V1_CONTRACT.md §5 *names* this endpoint
// PUT /submissions/{id}/annotation, but the deployed API Gateway only
// routes PUT /submissions/{id}/path (dregsbane-web-cdk ApiStack). The
// backend submission-annotate Lambda serves BOTH body shapes (drawn
// path/hazards AND the {labels} review diff) at /path, so the client
// must PUT /path — an /annotation alias would be a CDK change.
export function annotateSubmission(
  id: string,
  annotation: AnnotationPayload,
): Promise<Submission> {
  return request<Submission>(`/submissions/${encodeURIComponent(id)}/path`, {
    method: 'PUT',
    body: JSON.stringify(annotation),
  });
}

/**
 * Review-labels body (V1 contract §5), sent to PUT /submissions/{id}/path
 * (see route NOTE above) by /review on session confirm — one PUT per
 * photo. The existing path/hazard AnnotationPayload stays valid on the
 * same endpoint.
 */
export interface LabelsPayload {
  /** What the model proposed (`[]` if inference produced nothing). */
  suggested: Detection[];
  /** Final human-confirmed list; null when skipped=true. */
  edited: Detection[] | null;
  /** True when the user reviewed and excluded this photo. */
  skipped?: boolean;
  model_version?: string;
}

/**
 * Persist the review-labels diff for one photo. The backend writes
 * submissions.labels + labels_updated_at and removes the review-queue
 * row for this submission. Response body is not depended upon.
 */
export function saveSubmissionLabels(
  id: string,
  labels: LabelsPayload,
): Promise<unknown> {
  return request<unknown>(`/submissions/${encodeURIComponent(id)}/path`, {
    method: 'PUT',
    body: JSON.stringify({ labels }),
  });
}

export async function getMySubmissions(): Promise<Submission[]> {
  // The endpoint returns { submissions: [...] } — wrapped so the response
  // has somewhere to put future top-level fields (pagination cursor,
  // pending-count, etc.) without another breaking type change.
  const res = await request<{ submissions: Submission[] }>('/submissions/mine');
  return res.submissions;
}

// ─── Review queue (contributor capture-and-review flow) ────────────────────
//
// Per docs/contributor-capture-and-review-spec.md §7.2. The review-queue
// Lambda lives in dregsbane-web-backend (PR #19) and is wired by the cdk
// PR #16. Signed URLs are 1-hour TTL; the review client refetches the
// manifest if URLs expire mid-session.

export interface ReviewQueuePhoto {
  photoId: string;
  status: 'pending_inference' | 'ready_for_review' | 'inference_failed';
  /** Signed S3 GET URL for the photo bytes. */
  photoUrl: string;
  /** Signed S3 GET URL for the suggestions sidecar JSON. Absent when
   *  status !== 'ready_for_review' (no inference output yet). */
  suggestionsUrl?: string;
  /**
   * Advisory: the upload scan saw somebody in this shot.
   *
   * Three-valued on purpose. `true`/`false` are a scan result; ABSENT
   * means no scan ran — an older queue row, or a scan that failed. Absent
   * must never render as "nobody in frame", or a detector outage would
   * quietly look like a clean bill of health for every photo taken during
   * it.
   */
  personDetected?: boolean;
}

export interface ReviewQueueSession {
  sessionId: string;
  /** ISO 8601 capture time of the earliest photo in the session. */
  capturedAt: string;
  sessionNote?: string;
  photoCount: number;
  photos: ReviewQueuePhoto[];
}

export interface ReviewQueueManifest {
  pending: number;
  sessions: ReviewQueueSession[];
}

export function fetchReviewQueue(): Promise<ReviewQueueManifest> {
  return request<ReviewQueueManifest>('/api/review/queue');
}

// ─── Public label-review game ───────────────────────────────────────────────
//
// Distinct from the self-review queue above: reviewers here label OTHER
// users' submissions. Never routes through saveSubmissionLabels/
// `/submissions/{id}/path` — that endpoint mutates the *owning*
// contributor's own record. label-review-submit writes to a separate
// label_reviews table instead (migration 0010, dregsbane-web-backend).

export interface LabelQueueItem {
  submissionId: string;
  /** Signed S3 GET URL for the photo bytes. */
  photoUrl: string;
  /**
   * Presigned small-WebP thumbnail, when the backend has derived one.
   * OPTIONAL twice over: old photos predate thumbnail generation (the
   * field is absent), and a present URL can still 404 (the derivative
   * job is async). Consumers must fall back to `photoUrl` on either.
   */
  thumbUrl?: string;
  /** Best-available starting detections to correct — the contributor's
   *  own edited list if they reviewed it, else the raw model
   *  suggestions, else []. */
  suggested: Detection[];
  /** 'draw' when `suggested` is empty (nothing to correct against). */
  taskType: 'correct' | 'draw';
  /**
   * Credit line for photos that are not a contributor's own — the CC BY
   * attribution on calibration seeds. Absent on ordinary submissions.
   */
  attribution?: string;
  /**
   * Whether this photo has a contributor the reviewer could block.
   *
   * Optional only for wire-compatibility with a backend that predates the
   * field; consumers must treat absent as FALSE. The backend skips the
   * block for authorless submissions (calibration seeds, and anything
   * anonymised by account deletion), so offering the control for them
   * produces a success message and an empty Blocked-contributors list.
   */
  blockable?: boolean;
}

export interface LabelQueueManifest {
  items: LabelQueueItem[];
  /** Reciprocity counter, not a leaderboard — no per-user rank. */
  progress: { verifiedCount: number; milestoneTarget: number };
}

/**
 * Fetch the public label-review queue, optionally scoped to one
 * watershed (`?huc12=` — the hotspot-popup CTA routes reviewers to the
 * photos of the place they just tapped) and/or ordered closest-first by
 * the reviewer's coordinates (`?lat=&lng=`). No opts = the global
 * queue, byte-identical request to what shipped before scoping existed.
 */
export function fetchLabelQueue(
  opts: { huc12?: string; lat?: number; lng?: number } = {},
): Promise<LabelQueueManifest> {
  const params = new URLSearchParams();
  if (opts.huc12) params.set('huc12', opts.huc12);
  if (opts.lat !== undefined && opts.lng !== undefined) {
    params.set('lat', String(opts.lat));
    params.set('lng', String(opts.lng));
  }
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  return request<LabelQueueManifest>(`/api/label-review/queue${qs}`);
}

export interface LabelReviewPayload {
  detections: Detection[];
  task_type: 'correct' | 'draw';
}

export function submitLabelReview(
  submissionId: string,
  payload: LabelReviewPayload,
): Promise<{ ok: boolean; consensus_reached: boolean }> {
  return request<{ ok: boolean; consensus_reached: boolean }>(
    `/api/label-review/${encodeURIComponent(submissionId)}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

// ─── Content moderation (App Store Guideline 1.2) ──────────────────────────
//
// The /label queue above serves OTHER contributors' photos, so the app
// carries user-generated content a reviewer sees before any human at
// DregsBane does. Guideline 1.2 requires a way to flag that content, a
// way to block the person who posted it, and filtering BEFORE the content
// is public — the backend answers the last one by quarantining on the
// FIRST report (moderation-report Lambda), which is why the response says
// `quarantined` rather than "queued for review".
//
// Nothing in this section ever exposes WHO posted a photo. The reporter
// blocks a contributor by naming the SUBMISSION; the backend derives the
// author. That is the whole reason blockContributor takes a submission id
// and not a user id.

/**
 * Wire enum for `reason`. Plain-language labels live in
 * components/ReportDialog.tsx — these snake_case values never face a
 * contributor (AGENTS.md).
 */
export type ReportReason =
  | 'objectionable'
  | 'personal_info'
  | 'not_litter'
  | 'spam'
  | 'other';

export interface ReportContentPayload {
  /**
   * Either the bare submission ULID (what the /label queue serves as
   * `submissionId`) or the map's `sub-<ulid>` observation id. The backend
   * strips one leading `sub-` — the two surfaces that can report content
   * name the same row differently and neither should have to know that.
   */
  submission_id: string;
  reason: ReportReason;
  /** Optional free text, ≤ 500 chars (the server re-validates). */
  note?: string;
  /** Also stop being served this contributor's photos. */
  block_contributor?: boolean;
}

/**
 * POST /api/moderation/report — 201 with `quarantined: true` once the
 * content is out of public view.
 *
 * 409 `already_reported` is NOT a synonym for 201. The uniqueness the
 * server enforces is (reporter, submission) for all time, not per open
 * report, so a 409 also covers the case where an operator reviewed the
 * first report and dismissed it: the content is live, the second filing
 * inserts nothing, and nothing lands back in the operator queue. It is
 * still not a failure the user caused — treat it as "your report is on
 * file" and say only that (components/ReportDialog.tsx's two exported
 * strings are the two sentences), never "this is hidden".
 */
export function reportContent(
  payload: ReportContentPayload,
): Promise<{ ok: boolean; quarantined: boolean; blocked?: boolean }> {
  // `blocked` was previously dropped on the floor here, which is how the
  // UI came to claim a block that the backend had skipped: it refuses to
  // block when the submission has no contributor, says so in the
  // response, and nobody was reading it.
  return request<{ ok: boolean; quarantined: boolean; blocked?: boolean }>('/api/moderation/report', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * The open-data licence grant, served by the backend rather than shipped
 * in this bundle.
 *
 * The words a contributor reads and the version recorded against their
 * name have to be the same thing, and they stop being the same thing the
 * moment a deployed bundle and a deployed backend can disagree about what
 * the current wording is. So the server sends the text; this client only
 * renders it.
 */
export interface LicenceGrantText {
  licenceId: string;
  version: string;
  deedUrl: string;
  legalCodeUrl: string;
  grant: string;
  /** Why a photo and a coordinate need different instruments. */
  whyTwoLicences: string;
  /**
   * The second grant. Photos are copyrightable works and carry CC BY-SA;
   * the observation database is facts, which copyright licences do not fit
   * — ODbL exists for that. No photos are shared under it.
   */
  dataLicence: { id: string; url: string; covers: string; goesTo: string };
  youAreFreeTo: string[];
  underTheFollowingTerms: string[];
  whatThisCovers: string[];
  whereItGoes: string[];
  irrevocability: string;
}

export function getLicenceGrant(): Promise<{ grant: LicenceGrantText; accepted: boolean }> {
  return request<{ grant: LicenceGrantText; accepted: boolean }>('/api/licence');
}

/**
 * Records agreement to whatever the server considers current. Sends no
 * body on purpose — the version is not the client's to assert.
 */
export function acceptLicenceGrant(): Promise<{ accepted: boolean; version: string }> {
  return request<{ accepted: boolean; version: string }>('/api/licence', { method: 'POST' });
}

export interface BlockedContributor {
  /**
   * Per-blocker opaque handle, and the ONLY thing this client holds about
   * a blocked person. It is an HMAC the API derives over (blocker,
   * blocked) — not a user id — so it identifies the block without ever
   * naming the human, and it cannot be replayed against anyone else's
   * blocks. Safe to render only as a list key; there is nothing here to
   * display.
   */
  id: string;
  created_at: string;
}

/**
 * GET /api/moderation/blocks — who this user has blocked.
 *
 * NO IDENTITY COMES OUT OF HERE, by construction on both sides. The
 * blocks a person holds were almost all made from the /label queue,
 * which serves photos with zero attribution — so echoing a contributor's
 * name or user id back on /account would turn "report + block everything
 * I am served" into a way to learn who photographed a given GPS
 * location. /terms says in writing that blocking never reveals who
 * anyone is; the API now enforces that by sending only the handle, and
 * this projection keeps the client honest if that ever regresses.
 */
export function listBlocks(): Promise<BlockedContributor[]> {
  return request<{ blocks: Array<Record<string, unknown>> }>(
    '/api/moderation/blocks',
  ).then((r) =>
    // A projection, not a cast: anything the wire grows beyond the handle
    // is dropped HERE, at the boundary, so no component can render it by
    // reaching for a field that happens to be on the object.
    (r.blocks ?? []).map((b) => ({
      id: String(b.id ?? ''),
      created_at: String(b.created_at ?? ''),
    })),
  );
}

/**
 * POST /api/moderation/blocks — block by SUBMISSION, never by user id, so
 * the blocker never learns an identity they hadn't already seen. 409
 * `no_contributor` means the submission has no author left to block (an
 * anonymised account), which is not a failure the user can act on.
 */
export function blockContributor(submissionId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/moderation/blocks', {
    method: 'POST',
    body: JSON.stringify({ submission_id: submissionId }),
  });
}

/**
 * DELETE /api/moderation/blocks/{id}, where `id` is the handle from
 * listBlocks — never a user id, which this client never receives. The
 * API re-derives the handle against the caller's own rows, so a handle
 * lifted from someone else's account resolves to nothing. Idempotent:
 * unblocking twice is a success.
 */
export function unblockContributor(blockHandle: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/moderation/blocks/${encodeURIComponent(blockHandle)}`,
    { method: 'DELETE' },
  );
}

export function prefillFromOSM(lat: number, lon: number): Promise<OsmPrefill> {
  const qs = `lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`;
  return request<OsmPrefill>(`/prefill?${qs}`);
}

export interface TrailStats {
  total_submissions: number;
  total_contributors: number;
  hotspot_count: number;
}

export function getStats(): Promise<TrailStats> {
  return request<TrailStats>('/stats/trail');
}

// ─── Trail data layer (Aurora-backed; manifest pattern) ────────────────────
// See dregsbane-web-trail/docs/aurora-migration-proposal.md.
//
// /api/trail/manifest churns at the poll cadence (Cache-Control: max-age=5,
// stale-while-revalidate=30). All other endpoints carry a `version` segment
// in the path that makes them immutable per version — a manifest bump
// produces new URLs that miss CloudFront cache once then are permanent.

/**
 * Loose GeoJSON shape — avoids needing @types/geojson at build time, and
 * the runtime payloads from /api/trail/* aren't trying to be polymorphic.
 */
export interface GeoFeatureCollection<P = Record<string, unknown>> {
  type: 'FeatureCollection';
  metadata?: Record<string, unknown>;
  features: Array<{
    type: 'Feature';
    id?: string;
    geometry: { type: string; coordinates: unknown };
    properties: P;
  }>;
}

export interface TrailManifest {
  version: string;
  updated_at: string;
  observation_count: number;
  huc12_count: number;
  huc12s: Array<{ huc12: string; name: string; state: string }>;
  /**
   * National-scale tile mode (optional during rollout). Presence of a
   * valid block switches /explore onto the MapLibre + PMTiles path;
   * absence keeps the legacy GeoJSON path. Validated by
   * lib/tile-mode.ts — never read this field directly.
   */
  tiles?: {
    version: string;
    observations_url: string;
    watersheds_url: string;
  };
  /**
   * Open-data commons snapshot (optional — absent until the backend's
   * export-commons script publishes one). Presence of a valid block
   * renders the Sources sheet's "Open data" download row; absence
   * hides it. Validated by lib/commons-mode.ts — never read this
   * field directly.
   */
  commons?: {
    version: string;
    license: string;
    observations_url: string;
    metadata_url: string;
  };
}

// These read endpoints are public; using `fetch` directly (not `request`)
// to skip credentials:'include' — the cookie isn't needed and including
// it forces the response Vary:Origin which complicates edge caching.
async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new ApiError(res.status, await safeBody(res));
  }
  return (await res.json()) as T;
}

export function fetchTrailManifest(): Promise<TrailManifest> {
  return publicGet<TrailManifest>('/api/trail/manifest');
}

export function fetchTrailWatersheds(version: string): Promise<GeoFeatureCollection> {
  return publicGet<GeoFeatureCollection>(
    `/api/trail/v/${encodeURIComponent(version)}/watersheds`,
  );
}

export interface TrailObservationFilters {
  /** "true" (default), "false", or "all". */
  verified?: 'true' | 'false' | 'all';
  source_type?: string;
  /** Defaults to true on the server. */
  include_points?: boolean;
}

export function fetchTrailObservations(
  version: string,
  bbox: { w: number; s: number; e: number; n: number },
  filters: TrailObservationFilters = {},
): Promise<GeoFeatureCollection> {
  const params = new URLSearchParams();
  params.set('bbox', `${bbox.w},${bbox.s},${bbox.e},${bbox.n}`);
  if (filters.verified) params.set('verified', filters.verified);
  if (filters.source_type) params.set('source_type', filters.source_type);
  if (filters.include_points === false) params.set('include_points', 'false');
  return publicGet<GeoFeatureCollection>(
    `/api/trail/v/${encodeURIComponent(version)}/observations?${params}`,
  );
}

export interface TrailWatershedContext {
  huc12: string;
  name: string;
  state: string;
  watershed_context: {
    intake_name?: string;
    intake_source?: string;
    served_communities?: string[];
    ultimate_outlet?: string;
    sources?: Array<{ fact: string; url: string }>;
    [k: string]: unknown;
  };
  impairment_status: {
    is_impaired?: boolean;
    impaired_au_count?: number;
    total_au_count?: number;
    top_causes?: Array<{ cause: string; impaired_au_count: number }>;
    [k: string]: unknown;
  } | null;
}

export function fetchTrailWatershedContext(
  version: string,
  huc12: string,
): Promise<TrailWatershedContext> {
  return publicGet<TrailWatershedContext>(
    `/api/trail/v/${encodeURIComponent(version)}/watershed-context?huc12=${encodeURIComponent(huc12)}`,
  );
}

export function fetchTrailHaloPoints(
  version: string,
  observationId: string,
): Promise<{ id: string; points: Array<[number, number]> }> {
  return publicGet<{ id: string; points: Array<[number, number]> }>(
    `/api/trail/v/${encodeURIComponent(version)}/observations/${encodeURIComponent(observationId)}/halo-points`,
  );
}

/**
 * Per-observation drill-down for the hotspot popup: what the photos of
 * this spot actually contain, plus how many photos exist. Only
 * user-submission pins (`sub-` ids) carry data — everything else 404s,
 * and the popup degrades to its pre-detail rendering (the caller in
 * explore-map-tiles.ts swallows the rejection).
 *
 * `label_summary` is null when no labels exist yet; `tier` states WHO
 * the item list is from ('model' = detector output, 'owner' = the
 * reporter's own labels, 'consensus' = independent reviewers agreed),
 * which the popup must surface honestly — a detector guess and a
 * human-verified list must never read the same.
 */
export interface ObservationDetail {
  id: string;
  photo_count: number;
  label_summary: {
    tier: 'model' | 'owner' | 'consensus';
    items: Array<{ class_label: string; count: number }>;
    total_items: number;
    model_version?: string;
    updated_at: string;
  } | null;
}

export function fetchObservationDetail(
  version: string,
  id: string,
): Promise<ObservationDetail> {
  return publicGet<ObservationDetail>(
    `/api/trail/v/${encodeURIComponent(version)}/observations/${encodeURIComponent(id)}`,
  );
}

/**
 * Fresh overlay (issue #83): the last-48h window of USER SUBMISSIONS as
 * a FeatureCollection in the exact feature shape /observations serves.
 * Version-keyed like the sibling reads — the segment is a cache key
 * only (immutable per manifest version at the edge); a manifest bump
 * mints a new URL, which is what makes ~5s cross-user visibility ride
 * the existing poll with no new polling loop. Callers go through
 * lib/fresh-overlay.ts (never-throws adapter) — tile mode must degrade
 * to tiles alone on any failure, not surface an error.
 */
export function fetchTrailFresh(version: string): Promise<GeoFeatureCollection> {
  return publicGet<GeoFeatureCollection>(
    `/api/trail/v/${encodeURIComponent(version)}/fresh`,
  );
}

/**
 * Extends CreateSubmissionResponse with the new manifest_version field
 * the backend now returns post-submit. The trail client uses this to
 * immediately fetch new versioned observations data instead of waiting
 * on the next manifest poll (≤5s wait → ~300ms).
 */
export interface CreateSubmissionResponseWithManifest extends CreateSubmissionResponse {
  manifest_version: string;
}
